import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  ChevronRight,
  CreditCard,
  Gem,
  Landmark,
  Minus,
  Plus,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../utils/api';
import paymentApi from '../../utils/paymentApi';
import { getPaymentErrorCode, paymentErrorMessage, paymentResponseError } from './paymentErrors';
import { getCurrentTeam } from './teamApi';
import PaymentChannelReadiness from './PaymentChannelReadiness';
import {
  canRequestPayment,
  DEFAULT_PAYMENT_CHANNELS,
  getPaymentChannels,
  getSandboxPaymentChannels,
  isPrototypePaymentTestEnabled,
  opensCashierWindow,
  paymentCreateEndpoint,
  paymentMethodHint,
  paymentMethodLabel,
  paymentPrefix,
  UNIONPAY_B2B_CODE,
} from './paymentChannels';

const CREDIT_PACKAGE_URL = '/credit-package/list';
const PACKAGE_TONES = ['plain', 'lavender', 'ink', 'sage'];
const TIER_META = {
  1: {
    label: '日卡',
    labelEn: 'Daily',
    validity: '当天',
    validityEn: 'Today',
    expiration: '当天有效；未使用点数将于次日 00:00 自动清除。',
    expirationEn: 'Valid today. Unused credits are cleared automatically at 00:00 the next day.',
  },
  2: {
    label: '周卡',
    labelEn: 'Weekly',
    validity: '1 周',
    validityEn: '1 week',
    expiration: '一周内有效；未使用点数将在购买满 7 天后的 00:00 自动清除。',
    expirationEn: 'Valid for one week. Unused credits are cleared automatically at 00:00 after 7 days.',
  },
  3: {
    label: '月卡',
    labelEn: 'Monthly',
    validity: '1个月',
    validityEn: '1 month',
    expiration: '1个月内有效；未使用点数将于下个月同日 00:00 自动清除。',
    expirationEn: 'Valid for one month. Unused credits are cleared automatically at 00:00 on the same date next month.',
  },
};

function tierValidityLabel(tier, english) {
  const meta = TIER_META[tier.tierType];
  if (meta) return english ? meta.validityEn : meta.validity;
  return english ? `${tier.validityDays} days` : `${tier.validityDays} 天`;
}

function tierExpirationNotice(tier, english) {
  const meta = TIER_META[tier.tierType];
  if (meta) return english ? meta.expirationEn : meta.expiration;
  return english
    ? `Unused credits are cleared automatically after ${tier.validityDays} days.`
    : `未使用点数将在 ${tier.validityDays} 天有效期结束后自动清除。`;
}

function normalizeTier(rawTier) {
  const tier = rawTier && typeof rawTier === 'object' ? rawTier : {};
  const tierType = Number(tier.tier_type);
  return {
    tierId: Number(tier.id),
    tierType,
    tierName: TIER_META[tierType]?.label || '',
    tierNameEn: TIER_META[tierType]?.labelEn || '',
    price: Number(tier.price),
    credits: Number(tier.credits),
    validityDays: Number(tier.validity_days),
  };
}

function normalizePackage(rawPackage, index) {
  const item = rawPackage && typeof rawPackage === 'object' ? rawPackage : {};
  return {
    id: item.id,
    logo: String(item.logo || ''),
    name: String(item.name || ''),
    description: String(item.description || ''),
    planType: Number(item.plan_type || 1),
    tiers: Array.isArray(item.tiers)
      ? item.tiers.map(normalizeTier).filter(tier => tier.tierId
        && [1, 2, 3].includes(tier.tierType)
        && Number.isFinite(tier.price) && tier.price >= 0.01
        && Number.isInteger(tier.credits) && tier.credits > 0
        && Number.isInteger(tier.validityDays) && tier.validityDays > 0)
      : [],
    details: Array.isArray(item.details) ? item.details.map(value => String(value || '').trim()).filter(Boolean) : [],
    tags: Array.isArray(item.tags)
      ? item.tags.map(value => String(value || '').trim()).filter(Boolean).slice(0, 3)
      : [],
    updatedAt: String(item.updated_at || ''),
    tone: PACKAGE_TONES[index % PACKAGE_TONES.length],
  };
}

const packAmount = (product, quantity) => (product.price * quantity).toFixed(2);
const checkPaymentStatus = async (method, no) => {
  const res = await paymentApi.get(`${paymentPrefix(method)}/status`, { params: { pay_no: no } });
  if (Number(res?.data?.code) !== 200) throw paymentResponseError(res?.data);
  return res.data?.data?.status === 'success';
};

export function CheckoutDialog({ product, onClose, onPackageChanged, onViewOrders, english, user, team, teamName, preferredPaymentMethod }) {
  const [quantity, setQuantity] = useState(1);
  // 协议默认勾选，支付按钮可直接点击；用户取消勾选则禁用
  const [accepted, setAccepted] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('confirm'); // confirm | paying | success
  const [paymentMethod, setPaymentMethod] = useState(
    preferredPaymentMethod === UNIONPAY_B2B_CODE ? UNIONPAY_B2B_CODE : 'wechat',
  );
  const [paymentChannels, setPaymentChannels] = useState(DEFAULT_PAYMENT_CHANNELS);
  const [paymentChannelStatus, setPaymentChannelStatus] = useState('loading');
  const [payNo, setPayNo] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [codeUrl, setCodeUrl] = useState('');
  const [wechatExpiresAt, setWechatExpiresAt] = useState(null);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [checking, setChecking] = useState(false);
  const [closingPayment, setClosingPayment] = useState(false);
  const pollTimer = useRef(null);
  const channelRequestRef = useRef(0);
  const livePaymentTest = isPrototypePaymentTestEnabled();

  useEffect(() => {
    if (step !== 'success') return;
    setClosingPayment(false);
    window.dispatchEvent(new Event('credits-updated'));
  }, [step]);

  const totalCredits = product.credits * quantity;
  const totalAmount = packAmount(product, quantity);
  const visiblePaymentChannels = paymentChannels.filter(channel => channel.visible);
  const selectedPaymentChannel = paymentChannels.find(channel => channel.code === paymentMethod);

  const loadPaymentChannels = useCallback(async () => {
    const requestId = channelRequestRef.current + 1;
    channelRequestRef.current = requestId;
    setPaymentChannelStatus('loading');
    try {
      const channels = await (livePaymentTest ? getSandboxPaymentChannels() : getPaymentChannels());
      if (channelRequestRef.current !== requestId) return;
      setPaymentChannels(channels);
      setPaymentMethod(currentCode => (
        channels.find(channel => channel.code === currentCode && channel.visible)
          ? currentCode
          : channels.find(channel => channel.visible && channel.enabled)?.code
            || channels.find(channel => channel.visible)?.code
            || ''
      ));
      setPaymentChannelStatus('ready');
    } catch {
      if (channelRequestRef.current !== requestId) return;
      setPaymentChannels(DEFAULT_PAYMENT_CHANNELS);
      setPaymentChannelStatus('error');
    }
  }, [livePaymentTest]);

  useEffect(() => {
    loadPaymentChannels();
    return () => { channelRequestRef.current += 1; };
  }, [loadPaymentChannels]);

  const cancelPayment = useCallback(async () => {
    if (step !== 'paying' || !payNo) {
      onClose();
      return;
    }
    if (paymentMethod !== 'wechat') {
      onClose();
      return;
    }
    setClosingPayment(true);
    setError('');
    try {
      const response = await paymentApi.post('/wechat-pay/cancel', { pay_no: payNo });
      if (Number(response?.data?.code) !== 200) {
        throw paymentResponseError(response?.data);
      }
      if (response?.data?.data?.settled) {
        setStep('success');
        return;
      }
      onClose();
    } catch (requestError) {
      setError(paymentErrorMessage(requestError, english, 'cancel'));
      setClosingPayment(false);
    }
  }, [english, onClose, payNo, paymentMethod, step]);

  const closeCheckout = useCallback(() => {
    if (closingPayment) return;
    cancelPayment();
  }, [cancelPayment, closingPayment]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = event => {
      if (event.key === 'Escape') closeCheckout();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [closeCheckout]);

  // 正常支付轮询本地状态；本机银联测试定期主动查单，避免临时公网回调抖动后订单长期 pending。
  useEffect(() => {
    if (step !== 'paying' || !payNo) return undefined;
    let tries = 0;
    let stopped = false;
    pollTimer.current = setInterval(async () => {
      tries += 1;
      if (tries > 100) {
        clearInterval(pollTimer.current);
        setStatusError(getErrorMessage(english ? 'Automatic checking has paused. Check the payment result below or in Billing History. If charged, do not pay again.' : '自动查询已暂停，请点击下方按钮查询支付结果，或到「账单记录」查看。若已扣款，请勿重复付款。', '自动查询已暂停，请点击下方按钮查询支付结果，或到「账单记录」查看。若已扣款，请勿重复付款。'));
        return;
      }
      try {
        let paid = false;
        if (livePaymentTest && paymentMethod === UNIONPAY_B2B_CODE) {
          const response = await paymentApi.get('/unionpay-b2b/query', { params: { pay_no: payNo } });
          if (Number(response?.data?.code) !== 200) throw paymentResponseError(response?.data);
          paid = Number(response?.data?.code) === 200
            && (response?.data?.data?.settled || response?.data?.data?.status === 'success');
        } else {
          paid = await checkPaymentStatus(paymentMethod, payNo);
        }
        if (stopped) return;
        setStatusError('');
        if (paid) {
          clearInterval(pollTimer.current);
          setStep('success');
        }
      } catch (err) {
        if (!stopped) setStatusError(paymentErrorMessage(err, english, 'query'));
      }
    }, livePaymentTest ? 7000 : 3000);
    return () => { stopped = true; clearInterval(pollTimer.current); };
  }, [english, livePaymentTest, step, payNo, paymentMethod]);

  useEffect(() => {
    if (step !== 'paying' || paymentMethod !== 'wechat' || !codeUrl || !wechatExpiresAt) {
      return undefined;
    }
    const expiresAtMs = new Date(wechatExpiresAt).getTime();
    if (!Number.isFinite(expiresAtMs)) return undefined;
    const delay = expiresAtMs - Date.now();
    if (delay <= 0) {
      setCodeUrl('');
      setPayNo('');
      setWechatExpiresAt(null);
      setStep('confirm');
      setError('微信二维码已过期，请重新生成支付二维码');
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setCodeUrl('');
      setPayNo('');
      setWechatExpiresAt(null);
      setStep('confirm');
      setError('微信二维码已过期，请重新生成支付二维码');
    }, delay);
    return () => window.clearTimeout(timer);
  }, [codeUrl, english, paymentMethod, step, wechatExpiresAt]);

  const handlePay = async () => {
    if (!livePaymentTest && !user?.id) {
      setError(getErrorMessage(english ? 'Please sign in before purchasing.' : '未获取到用户信息，请重新登录后再充值', '未获取到用户信息，请重新登录后再充值'));
      return;
    }
    if (!canRequestPayment(selectedPaymentChannel) || !paymentPrefix(paymentMethod)) {
      setError(getErrorMessage(paymentMethodHint(selectedPaymentChannel, english)));
      return;
    }
    setSubmitting(true);
    setError('');
    setStatusError('');
    // 跳转式收银台需同步开新窗口，避免浏览器拦截；微信 Native 支付直接展示二维码。
    const payWindow = opensCashierWindow(paymentMethod) ? window.open('', '_blank') : null;
    if (opensCashierWindow(paymentMethod) && !payWindow) {
      setSubmitting(false);
      setError(getErrorMessage(english ? 'Please allow pop-ups and try again.' : '支付窗口被浏览器拦截，请允许弹窗后重试', '支付窗口被浏览器拦截，请允许弹窗后重试'));
      return;
    }
    try {
      const res = await paymentApi.post(paymentCreateEndpoint(paymentMethod), {
        order_type: team ? 'team_credit_package' : 'credit_package',
        package_id: product.id,
        tier_type: product.tierType,
        quantity,
        package_updated_at: product.updatedAt,
        ...(paymentMethod === 'alipay' ? { scene: 'page' } : {}),
      });
      const body = res?.data;
      if (Number(body?.code) !== 200) throw paymentResponseError(body);
      const serverAmount = Number(body.data?.amount);
      const serverCredits = Number(body.data?.credits);
      if (!Number.isFinite(serverAmount)
        || serverAmount.toFixed(2) !== totalAmount
        || serverCredits !== totalCredits
        || Number(body.data?.tier_type) !== product.tierType
        || Number(body.data?.validity_days) !== product.validityDays) {
        const changedError = new Error(english ? 'The package changed. Please confirm it again.' : '套餐信息已更新，请重新确认');
        changedError.code = 'PACKAGE_CHANGED';
        throw changedError;
      }
      if (!body.data?.pay_no) throw new Error(english ? 'The payment number is missing.' : '支付单号缺失，请稍后重试');
      setOrderNo(String(body.data?.order_no || ''));
      if (body.data?.status === 'success') {
        payWindow?.close();
        setPayNo(body.data.pay_no);
        setStep('success');
        return;
      }
      if (paymentMethod === 'wechat') {
        if (!body.data?.code_url) throw new Error('微信支付未返回二维码');
        setCodeUrl(body.data.code_url);
        setWechatExpiresAt(body.data?.expires_at || null);
      } else {
        if (!body.data?.form) throw new Error(english ? 'Missing cashier form.' : '支付收银台信息缺失');
        payWindow.document.write(body.data.form);
        payWindow.document.close();
      }
      setPayNo(body.data.pay_no);
      setStep('paying');
    } catch (err) {
      if (payWindow) payWindow.close();
      const errorCode = getPaymentErrorCode(err);
      if (errorCode === 'PACKAGE_CHANGED' && onPackageChanged) {
        onPackageChanged();
        return;
      }
      setError(paymentErrorMessage(err, english));
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualCheck = async () => {
    if (checking) return;
    setChecking(true);
    setError('');
    setStatusError('');
    try {
      const res = await paymentApi.get(`${paymentPrefix(paymentMethod)}/query`, { params: { pay_no: payNo } });
      if (Number(res?.data?.code) !== 200) throw paymentResponseError(res?.data);
      const paid = Number(res.data?.code) === 200
        && (res.data?.data?.settled || res.data?.data?.status === 'success');
      if (paid || await checkPaymentStatus(paymentMethod, payNo)) {
        setStep('success');
      } else {
        setError(getErrorMessage(english ? 'Payment is not confirmed yet. Check again shortly or view Billing History. If charged, do not pay again.' : '暂未查询到支付结果，入账可能稍有延迟，请稍后再查或到「账单记录」查看。若已扣款，请勿重复付款。', '暂未查询到支付结果，入账可能稍有延迟，请稍后再查或到「账单记录」查看。若已扣款，请勿重复付款。'));
      }
    } catch (err) {
      setError(paymentErrorMessage(err, english, 'query'));
    } finally {
      setChecking(false);
    }
  };

  return createPortal((
    <div className="billing-checkout-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) closeCheckout();
    }}>
      <section className="billing-checkout" role="dialog" aria-modal="true" aria-labelledby="checkout-title">
        <header>
          <div>
            <span className="account-eyebrow">ORDER CONFIRMATION</span>
            <h2 id="checkout-title">{english ? 'Confirm Purchase' : '确认充值'}</h2>
          </div>
          <button type="button" onClick={closeCheckout} disabled={closingPayment} aria-label={english ? 'Close checkout' : '关闭结算'}><X size={17} /></button>
        </header>

        <div className="billing-checkout__body">
          {livePaymentTest && (
            <p className="billing-payment-test-notice is-enabled" role="status">
              <ShieldCheck size={14} />
              <span>{english
                ? 'Test mode: a test order will be created. No real funds will be charged.'
                : '测试模式：将创建测试订单，不会扣除真实资金。'}</span>
            </p>
          )}
          <section className="billing-checkout-product">
            <span>{product.logo ? <img src={product.logo} alt="" /> : <Gem size={20} />}</span>
            <div>
              <h3>{product.name}</h3>
              <p>
                {english ? product.tierNameEn : product.tierName}
                {' · '}{tierValidityLabel(product, english)}
              </p>
            </div>
            <div className="billing-checkout-product__quantity">
              <button
                type="button"
                aria-label={english ? 'Decrease quantity' : '减少数量'}
                onClick={() => setQuantity(current => Math.max(1, current - 1))}
                disabled={quantity === 1 || step !== 'confirm'}
              ><Minus size={12} /></button>
              <strong>{quantity}</strong>
              <button
                type="button"
                aria-label={english ? 'Increase quantity' : '增加数量'}
                onClick={() => setQuantity(current => Math.min(9, current + 1))}
                disabled={step !== 'confirm'}
              ><Plus size={12} /></button>
            </div>
          </section>

          <section className="billing-checkout-section">
            <div className="billing-checkout-section__heading">
              <h3>{english ? 'Destination wallet' : '到账钱包'}</h3>
              <span>{english ? 'Determined by the selected credit pack' : '归属由点数包类型决定'}</span>
            </div>
            <div className="billing-wallet-destination">
              {team ? <Users size={16} aria-hidden="true" /> : <Wallet size={16} aria-hidden="true" />}
              <span>
                <strong>{team
                  ? (teamName || (english ? 'Current team wallet' : '当前团队钱包'))
                  : (english ? 'Personal wallet' : '个人钱包')}</strong>
                <small>{team
                  ? (english ? 'Team credits go to the current team at checkout and are shared by its members.' : '团队点数到账至下单时的当前团队，供团队成员共享使用。')
                  : (english ? 'Personal credits go to your account and can only be used by you.' : '个人点数到账至你的账户，仅供本人使用。')}</small>
              </span>
            </div>
          </section>

          <section className="billing-checkout-section">
            <div className="billing-checkout-section__heading">
              <h3>{english ? 'Payment method' : '支付方式'}</h3>
              <span role="status" aria-live="polite">{paymentMethodHint(selectedPaymentChannel, english)}</span>
            </div>
            <div className="billing-payment-methods">
              {visiblePaymentChannels.map(channel => (
                <button
                  type="button"
                  key={channel.code}
                  className={`${paymentMethod === channel.code ? 'is-selected' : ''}${!channel.enabled && channel.code !== 'alipay' ? ' is-unavailable' : ''}`.trim()}
                  aria-pressed={paymentMethod === channel.code}
                  disabled={step !== 'confirm' || submitting}
                  title={paymentMethodHint(channel, english)}
                  onClick={() => {
                    setError('');
                    setPaymentMethod(channel.code);
                  }}
                >
                  {channel.code === 'wechat'
                    ? <span className="billing-payment-methods__wechat">{english ? 'W' : '微'}</span>
                    : channel.code === UNIONPAY_B2B_CODE
                      ? <span className="billing-payment-methods__bank"><Landmark size={15} /></span>
                      : <CreditCard size={17} />}
                  <span>
                    <strong>{paymentMethodLabel(channel.code, english)}</strong>
                    <small>{english
                      ? (channel.code === 'wechat' ? 'Scan a QR code' : channel.code === 'alipay' ? 'Open Alipay cashier' : paymentMethodHint(channel, true))
                      : paymentMethodHint(channel, false)}</small>
                  </span>
                  {channel.code === UNIONPAY_B2B_CODE && <em className={`billing-payment-methods__badge ${channel.enabled ? 'is-ready' : ''}`}>{channel.enabled ? (english ? 'Test ready' : '可测试') : ('暂不可用')}</em>}
                  {channel.code !== UNIONPAY_B2B_CODE && channel.code !== 'alipay' && !channel.enabled && <em className="billing-payment-methods__badge">{'暂不可用'}</em>}
                </button>
              ))}
            </div>
            <PaymentChannelReadiness
              channel={selectedPaymentChannel}
              status={paymentChannelStatus}
              onRetry={loadPaymentChannels}
              english={english}
            />
          </section>

          <section className="billing-order-summary">
            <div><span>{english ? 'Selected tier' : '所选档位'}</span><strong>{english ? product.tierNameEn : product.tierName}</strong></div>
            <div><span>{english ? 'Validity' : '有效期'}</span><strong>{tierValidityLabel(product, english)}</strong></div>
            <div><span>{english ? 'Credits' : '点数'}</span><strong>{totalCredits.toLocaleString(english ? 'en-US' : 'zh-CN')}</strong></div>
            <div><span>{english ? 'Package price' : '套餐单价'}</span><strong>¥{product.price.toFixed(2)}/{english ? 'pack' : '份'}</strong></div>
            <div><span>{english ? 'Quantity' : '数量'}</span><strong>× {quantity}</strong></div>
            <div className="billing-order-summary__total"><span>{english ? 'Amount due' : '应付金额'}</span><strong>¥{totalAmount}</strong></div>
          </section>

          {step === 'paying' && paymentMethod === 'wechat' && codeUrl && (
            <section className="billing-wechat-qr" aria-live="polite">
              <span><QRCodeSVG value={codeUrl} size={154} level="M" title={english ? 'WeChat Pay QR code' : '微信支付二维码'} /></span>
              <div>
                <h3>{english ? 'Scan to pay in WeChat' : '请使用微信扫码支付'}</h3>
                <p>{english ? 'Credits arrive after payment is confirmed. Keep this page open while paying.' : '确认付款成功后点数自动到账，请在付款期间保持页面打开。'}</p>
                {wechatExpiresAt && <p className="billing-qr-expiry">{english ? 'QR code valid until' : '二维码有效期至'} {new Date(wechatExpiresAt).toLocaleTimeString(english ? 'en-US' : 'zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>}
                <code>{payNo}</code>
              </div>
            </section>
          )}

          {step === 'paying' && opensCashierWindow(paymentMethod) && (
            <section className="billing-redirect-payment" aria-live="polite">
              <span>{paymentMethod === UNIONPAY_B2B_CODE ? <Landmark size={19} /> : <CreditCard size={19} />}</span>
              <div>
                <h3>{livePaymentTest
                  ? (english ? 'UnionPay test cashier opened' : '银联测试收银台已打开')
                  : (english ? `${paymentMethodLabel(paymentMethod, true)} cashier opened` : `已打开${paymentMethodLabel(paymentMethod)}收银台`)}</h3>
                <p>{livePaymentTest
                  ? (english ? 'Test order created. Waiting for the payment result.' : '测试订单已创建，正在等待支付结果。')
                  : (english ? 'Complete the payment in the new window, then return here to verify the result.' : '请在新窗口完成付款，完成后返回此处查询支付结果。')}</p>
                {orderNo && <p>{english ? `Test order: ${orderNo}` : `测试订单号：${orderNo}`}</p>}
                <code>{payNo}</code>
              </div>
            </section>
          )}

          {step === 'success' && livePaymentTest && (
            <section className="billing-redirect-payment" aria-live="polite">
              <span><Check size={19} /></span>
              <div>
                <h3>{english ? 'Test payment confirmed' : '测试支付已确认'}</h3>
                <p>{english ? 'The order and payment record are now available in billing history.' : '订单和支付记录已更新，可前往账单记录查看。'}</p>
                {orderNo && <code>{orderNo}</code>}
              </div>
            </section>
          )}

          {step === 'confirm' && (
            <label className="billing-agreement">
              <input type="checkbox" checked={accepted} onChange={event => setAccepted(event.target.checked)} />
              <span><i>{accepted && <Check size={10} />}</i></span>
              <span>{english ? 'I agree to the Terms of Service, credit expiration, and refund policy.' : '我已阅读并同意服务协议、点数有效期和退款规则'}</span>
            </label>
          )}
        </div>

        {step !== 'success' && (error || statusError) && <p className="billing-checkout__error" role="alert" aria-atomic="true">{error || statusError}</p>}

        <footer>
          <div>
            <ShieldCheck size={14} />
            <span>{livePaymentTest && step === 'paying'
              ? (english ? 'Local test order created · confirming UnionPay result' : '本地测试订单已创建 · 正在确认银联结果')
              : (english ? 'Please confirm the amount due and credits received.' : '请确认应付金额及到账点数')}</span>
          </div>
          {step === 'confirm' && (
            <button
              type="button"
              disabled={!accepted || submitting || (paymentMethod === UNIONPAY_B2B_CODE && paymentChannelStatus !== 'ready') || !canRequestPayment(selectedPaymentChannel)}
              title={!canRequestPayment(selectedPaymentChannel) ? paymentMethodHint(selectedPaymentChannel, english) : undefined}
              onClick={handlePay}
            >
              {submitting
                ? (english ? 'Creating order…' : '正在创建订单…')
                : `${paymentMethodLabel(paymentMethod, english)} ¥${totalAmount}`}
            </button>
          )}
          {step === 'paying' && (
            livePaymentTest ? (
              <>
                <button type="button" className="billing-checkout-cancel" onClick={cancelPayment}>
                  {english ? 'Close' : '关闭'}
                </button>
                <button type="button" onClick={handleManualCheck} disabled={checking}>
                  {checking ? (english ? 'Checking…' : '正在查询…') : (english ? 'Check UnionPay result' : '查询银联结果')}
                </button>
              </>
            ) : <>
              <button type="button" className="billing-checkout-cancel" onClick={cancelPayment} disabled={closingPayment}>
                {closingPayment ? (english ? 'Cancelling…' : '正在取消…') : (english ? 'Cancel payment' : '取消支付')}
              </button>
              <button type="button" onClick={handleManualCheck} disabled={checking}>
                {checking ? (english ? 'Checking…' : '正在查询…') : (english ? 'I have completed the payment' : '我已完成支付')}
              </button>
            </>
          )}
          {step === 'success' && (
            livePaymentTest ? (
              <button type="button" onClick={onViewOrders || onClose}>
                {english ? 'View test order' : '查看测试订单'}
              </button>
            ) : (
              <button type="button" onClick={onClose}>
                {english ? 'Payment received · Done' : '充值成功，完成'}
              </button>
            )
          )}
        </footer>
      </section>
    </div>
  ), document.body);
}

export default function CreditStorePanel({ user: userProp, english = false, initialPlanType = 1, onViewOrders }) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [user, setUser] = useState(userProp || null);
  const [activePlanType, setActivePlanType] = useState(initialPlanType);
  const [selectedTiers, setSelectedTiers] = useState({});
  const [packages, setPackages] = useState([]);
  const [catalogStatus, setCatalogStatus] = useState('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [catalogNotice, setCatalogNotice] = useState('');
  const [currentTeam, setCurrentTeam] = useState(null);
  const [balanceRevision, setBalanceRevision] = useState(0);

  const fetchUser = useCallback(() => {
    api.get('/users/me').then(res => setUser(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const refreshBalance = () => { fetchUser(); setBalanceRevision(value => value + 1); };
    window.addEventListener('credits-updated', refreshBalance);
    return () => window.removeEventListener('credits-updated', refreshBalance);
  }, [fetchUser]);

  useEffect(() => {
    if (userProp) {
      setUser(userProp);
    } else {
      fetchUser();
    }
  }, [userProp, fetchUser]);

  useEffect(() => {
    setActivePlanType(initialPlanType);
  }, [initialPlanType]);

  useEffect(() => {
    let active = true;
    setCatalogStatus('loading');
    setSelectedTiers({});
    setSelectedProduct(null);
    paymentApi.get(CREDIT_PACKAGE_URL, { params: { plan_type: activePlanType } })
      .then(response => {
        const body = response?.data;
        if (Number(body?.code) !== 200) {
          throw new Error(body?.message || 'CREDIT_PACKAGE_CATALOG_FAILED');
        }
        const rows = Array.isArray(body?.data?.list) ? body.data.list : [];
        const normalized = rows
          .map(normalizePackage)
          .filter(item => item.id && item.name && item.updatedAt && item.planType === activePlanType
            && item.tiers.length === 3);
        if (active) {
          setPackages(normalized);
          setCatalogStatus('ready');
        }
      })
      .catch(() => {
        if (active) {
          setPackages([]);
          setCatalogStatus('error');
        }
      });
    return () => { active = false; };
  }, [activePlanType, reloadKey]);

  useEffect(() => {
    if (activePlanType !== 2) {
      setCurrentTeam(null);
      return;
    }
    let active = true;
    getCurrentTeam().then(team => active && setCurrentTeam(team)).catch(() => active && setCurrentTeam(null));
    return () => { active = false; };
  }, [activePlanType, balanceRevision]);

  const handlePackageChanged = () => {
    setSelectedProduct(null);
    setCatalogNotice(english ? 'The package changed. Please confirm the latest details.' : '套餐信息已更新，请按最新价格和点数重新确认。');
    setReloadKey(value => value + 1);
  };

  const credits = Number(user?.credits || 0);
  const teamCatalog = activePlanType === 2;
  const canPayTeam = !teamCatalog || ['owner', 'admin'].includes(currentTeam?.role);
  const preferredPaymentMethod = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('payment') === UNIONPAY_B2B_CODE
    ? UNIONPAY_B2B_CODE
    : 'wechat';

  const selectedTierFor = pack => {
    const requestedType = Number(selectedTiers[String(pack.id)] || 1);
    return pack.tiers.find(tier => tier.tierType === requestedType) || pack.tiers[0];
  };

  const selectTier = (packageId, tierType) => {
    setSelectedTiers(current => ({ ...current, [String(packageId)]: tierType }));
  };

  return (
    <>
      <section className="account-page-heading">
        <div>
          <span className="account-eyebrow">CREDIT STORE</span>
          <h1>{english ? 'Credit Packs' : '点数套餐'}</h1>
          <p>{english ? 'Choose a personal or team credit pack, then confirm its validity and payment method.' : '选择个人或团队点数包，确认有效期与支付方式。'}</p>
        </div>
        <span className={`account-status ${teamCatalog ? 'account-status--neutral' : 'account-status--warm'}`}>
          {teamCatalog
            ? (english ? 'Current team checkout' : '当前团队充值')
            : (english ? 'Secure online payment' : '安全在线支付')}
        </span>
      </section>

      <section className="credit-store-wallet">
        <div>
          <span className="credit-store-wallet__icon"><Wallet size={19} /></span>
          <div>
            <span>{teamCatalog ? (english ? 'Current team balance' : '当前团队点数') : (english ? 'Personal wallet balance' : '个人钱包点数')}</span>
            <strong>{Number(teamCatalog ? (currentTeam?.credits || 0) : credits).toLocaleString(english ? 'en-US' : 'zh-CN')}</strong>
          </div>
        </div>
        <div>
          <span>{teamCatalog ? (english ? 'Orders always credit the current team saved at checkout.' : '订单固定记录并到账到下单时的当前团队。') : (english ? 'Balance includes purchases and grants.' : '余额包含购买与赠送点数。')}</span>
        </div>
      </section>

      <section className="credit-store-section">
        <div className="credit-store-section__heading">
          <div>
            <span className="account-eyebrow">ONE-TIME CREDIT PACKS</span>
            <h2>{english ? 'One-time Credit Packs' : '一次性点数包'}</h2>
            <p>{english ? 'No auto-renewal. Credits arrive after payment is confirmed.' : '不自动续费，确认付款成功后点数自动到账。'}</p>
          </div>
        </div>
        <div className="billing-membership-tabs" role="tablist" aria-label={english ? 'Credit package type' : '点数套餐类型'}>
          <button
            type="button"
            role="tab"
            aria-selected={activePlanType === 1}
            className={activePlanType === 1 ? 'is-active' : ''}
            onClick={() => setActivePlanType(1)}
          >
            {english ? 'Personal Packs' : '个人点数包'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activePlanType === 2}
            className={activePlanType === 2 ? 'is-active' : ''}
            onClick={() => setActivePlanType(2)}
          >
            {english ? 'Team Packs' : '团队点数包'}
          </button>
        </div>
        {catalogNotice && <p className="credit-store-catalog-notice" role="status">{catalogNotice}</p>}
        {catalogStatus === 'loading' ? (
          <section className="billing-plan-state" aria-live="polite">
            <RefreshCw size={18} />
            <strong>{english ? 'Loading credit packages…' : '正在加载点数套餐…'}</strong>
          </section>
        ) : catalogStatus === 'error' ? (
          <section className="billing-plan-state" role="alert">
            <strong>{'点数套餐加载失败'}</strong>
            <button type="button" onClick={() => setReloadKey(value => value + 1)}>
              <RefreshCw size={13} />{english ? 'Retry' : '重新加载'}
            </button>
          </section>
        ) : packages.length === 0 ? (
          <section className="billing-plan-state" aria-live="polite">
            <strong>{english ? 'No credit packages are available.' : '暂无可购买的点数套餐'}</strong>
          </section>
        ) : (
          <div className="credit-pack-grid">
            {packages.map(pack => {
              const selectedTier = selectedTierFor(pack);
              return (
                <article
                  key={pack.id}
                  className={`credit-pack-card credit-pack-card--${pack.tone}${pack.tags.length ? ' is-featured' : ''}`}
                >
                  <div className="credit-pack-card__header">
                    <div className="credit-pack-card__intro">
                      <div className="credit-pack-card__icon">
                        {pack.logo ? <img src={pack.logo} alt="" /> : <Sparkles size={18} />}
                      </div>
                      <h3>{pack.name}</h3>
                      {pack.description && <p>{pack.description}</p>}
                    </div>
                    {pack.tags.length > 0 && (
                      <div className="credit-pack-card__tags" aria-label={english ? 'Package tags' : '套餐标签'}>
                        {pack.tags.map(tag => <span key={tag}>{tag}</span>)}
                      </div>
                    )}
                  </div>
                  <div
                    className="billing-package-options billing-package-options--three"
                    role="radiogroup"
                    aria-label={english ? `${pack.name} validity tier` : `${pack.name}有效期档位`}
                  >
                    {pack.tiers.map(tier => (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selectedTier.tierType === tier.tierType}
                        className={selectedTier.tierType === tier.tierType ? 'is-active' : ''}
                        key={tier.tierType}
                        onClick={() => selectTier(pack.id, tier.tierType)}
                      >
                        <strong>{english ? tier.tierNameEn : tier.tierName}</strong>
                        <span>{tierValidityLabel(tier, english)}</span>
                      </button>
                    ))}
                  </div>
                  <div className="credit-pack-card__value">
                    <span>{english ? 'Credits received' : '到账点数'}</span>
                    <strong>{selectedTier.credits.toLocaleString(english ? 'en-US' : 'zh-CN')}</strong>
                  </div>
                  <div className="credit-pack-card__value">
                    <span>{english ? 'Price (CNY)' : '人民币售价'}</span>
                    <strong>¥{selectedTier.price.toFixed(2)}</strong>
                  </div>
                  <p className="credit-pack-card__validity">
                    {tierExpirationNotice(selectedTier, english)}
                  </p>
                  {pack.details.length > 0 && (
                    <ul>
                      {pack.details.map((detail, index) => (
                        <li key={`${pack.id}-${index}`}><Check size={12} />{detail}</li>
                      ))}
                    </ul>
                  )}
                  {canPayTeam && <button
                    type="button"
                    onClick={() => {
                      setCatalogNotice('');
                      setSelectedProduct({ ...pack, ...selectedTier });
                    }}
                  >
                    {teamCatalog ? (english ? 'Buy for current team' : '为当前团队充值') : (english ? 'Buy now' : '立即充值')}
                    <ChevronRight size={14} />
                  </button>}
                  {!canPayTeam && <p className="credit-pack-card__validity">{english ? 'Only the current team owner or admin can purchase.' : '仅当前团队的所有者或管理员可以购买。'}</p>}
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="credit-store-rules">
        <article>
          <span><ReceiptText size={17} /></span>
          <div><h3>{english ? 'Orders & fulfillment' : '订单与到账'}</h3><p>{english ? 'View payment results and credit delivery in Billing History. If you have been charged, do not pay again.' : '可在“账单记录”查看支付结果与点数到账情况。若已扣款，请勿重复支付。'}</p></div>
        </article>
        <article>
          <span><ShieldCheck size={17} /></span>
          <div><h3>{english ? 'Refund requests' : '退款申请'}</h3><p>{english ? 'Eligible credit packs can be submitted for refund review in My Packages. Refund availability depends on the original payment method.' : '符合条件的点数包可在“我的套餐”提交退款审核，能否原路退款以订单支付渠道实际支持为准。'}</p></div>
        </article>
      </section>

      {selectedProduct && (
        <CheckoutDialog
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onPackageChanged={handlePackageChanged}
          onViewOrders={onViewOrders}
          english={english}
          user={user}
          team={teamCatalog}
          teamName={currentTeam?.name}
          preferredPaymentMethod={preferredPaymentMethod}
        />
      )}
    </>
  );
}
